import { useDispatch, useSelector } from 'react-redux'
import { handleRequestFailure, makeRequest } from 'api/helpers'
import { setFetching } from 'redux/slices/api'
import FetchTypes from 'util/fetch-types'
import { TVisitType } from 'util/visit-types'
import { useCallback } from 'react'
import { getMember } from 'redux/selectors/members'
import { differenceInYears, parseISO } from 'date-fns'
import Member from 'types/member/member'
import { useMinorConsent } from './useMinorConsent'
import * as APM from 'util/apm'

// Type for the eligibility response (after snake_case to camelCase conversion)
export interface EligibilityResponse {
  eligible: boolean
  consentRequired: boolean
  consentTypeRequired?: 'parental' | 'self'
  reason?: string
  memberAge: number
  ageOfConsent: number
  existingConsent?: {
    type: 'parental' | 'self'
    status: string
  } | null
}

// Type for consent regulation from API
interface ConsentRegulation {
  state: string | null
  service: string | null
  ageOfConsent: number
  consentTypeAllowed: 'parental' | 'self'
}

export const usePreVisitRegulations = (memberId: string) => {
  const dispatch = useDispatch()
  const member: Member = useSelector(getMember(memberId))
  const { getMinorConsentByService } = useMinorConsent(memberId)

  // Calculate age from DOB
  const calculateAge = (dob: string): number => {
    if (!dob) return 0
    const birthDate = parseISO(dob)
    const today = new Date()
    return differenceInYears(today, birthDate)
  }

  // Select most specific regulation based on priority
  const selectMostSpecificRegulation = (
    regulations: ConsentRegulation[],
    state: string,
    service: string,
  ): ConsentRegulation | null => {
    if (!regulations || regulations.length === 0) {
      return null
    }

    // Priority: state+service > state only > service only > default
    const stateAndService = regulations.find((r) => r.state === state && r.service === service)
    if (stateAndService) {
      return stateAndService
    }

    const stateOnly = regulations.find((r) => r.state === state && !r.service)
    if (stateOnly) {
      return stateOnly
    }

    const serviceOnly = regulations.find((r) => !r.state && r.service === service)
    if (serviceOnly) {
      return serviceOnly
    }

    const defaultReg = regulations.find((r) => !r.state && !r.service)
    if (defaultReg) {
      return defaultReg
    }

    // Fallback to first regulation or default values
    const fallback = regulations[0] || { state: null, service: null, ageOfConsent: 18, consentTypeAllowed: 'parental' }
    return fallback
  }

  const checkServiceEligibility = useCallback(
    async (
      service: TVisitType,
      state: string,
    ): Promise<{
      success: boolean
      result: EligibilityResponse | null
    }> => {
      try {
        // Step 1: Fetch consent regulations (filtering by service only, state filtering done client-side)
        dispatch(setFetching({ fetchType: FetchTypes.getConsentRegulations, isFetching: true }))
        const regulationsParams = { params: { filter: { service } } }
        let regulations: ConsentRegulation[] = []

        const handleRegulationsSuccess = (data: ConsentRegulation[]) => {
          regulations = data
        }

        await makeRequest(
          FetchTypes.getConsentRegulations,
          regulationsParams,
          handleRegulationsSuccess,
          handleRequestFailure(dispatch),
        )
        dispatch(setFetching({ fetchType: FetchTypes.getConsentRegulations, isFetching: false }))

        // Step 2: Select the most specific regulation
        const regulation = selectMostSpecificRegulation(regulations, state, service) || {
          state: null,
          service: null,
          ageOfConsent: 18,
          consentTypeAllowed: 'parental' as const,
        }

        // Step 3: Calculate member age
        const memberAge = member?.dob ? calculateAge(member.dob) : 0

        // Step 4: Check if consent is required
        const consentRequired = memberAge < regulation.ageOfConsent

        // If no consent required, member is eligible
        if (!consentRequired) {
          return {
            success: true,
            result: {
              eligible: true,
              consentRequired: false,
              memberAge,
              ageOfConsent: regulation.ageOfConsent,
            },
          }
        }

        // Step 5: Check for existing consent (both old and new style)

        // Check old-style consent (consent_filename)
        const hasOldConsent =
          member?.consentFilename !== null && member?.consentFilename !== undefined && member?.consentFilename !== ''

        // Check new-style consent
        const { result: newConsents } = await getMinorConsentByService(service, state)

        // Check for completed consents
        const hasParentalConsent =
          newConsents?.some((consent) => consent.status === 'completed' && consent.type === 'parent') || false

        const hasSelfConsent =
          newConsents?.some((consent) => consent.status === 'completed' && consent.type === 'self') || false

        const hasNewConsent = regulation.consentTypeAllowed === 'parental' ? hasParentalConsent : hasSelfConsent

        // Step 6: Determine eligibility

        if (hasOldConsent || hasNewConsent) {
          const result = {
            eligible: true,
            consentRequired: true,
            consentTypeRequired: regulation.consentTypeAllowed,
            memberAge,
            ageOfConsent: regulation.ageOfConsent,
            existingConsent:
              hasOldConsent || hasNewConsent
                ? {
                    type: regulation.consentTypeAllowed,
                    status: 'completed',
                  }
                : null,
          }

          return {
            success: true,
            result,
          }
        }

        // Consent required but not present
        const result = {
          eligible: false,
          consentRequired: true,
          consentTypeRequired: regulation.consentTypeAllowed,
          reason: `${regulation.consentTypeAllowed}_consent_required`,
          memberAge,
          ageOfConsent: regulation.ageOfConsent,
        }

        return {
          success: true,
          result,
        }
      } catch (error) {
        console.error('PreVisitReg: ERROR in usePreVisitRegulations:', error)

        // Report to Sentry for monitoring
        APM.captureException(error as Error)

        // Calculate member age for error fallback
        const memberAge = member?.dob ? calculateAge(member.dob) : 0
        const isMinor = memberAge < 18

        // Only block minors when system fails - adults should be allowed through
        if (isMinor) {
          console.error('PreVisitReg: Blocking minor due to system error')
          return {
            success: false,
            result: {
              eligible: false,
              consentRequired: true,
              consentTypeRequired: 'parental',
              reason: 'system_error_minor_blocked',
              memberAge,
              ageOfConsent: 18,
            },
          }
        } else {
          console.error('PreVisitReg: Allowing adult through despite system error')
          return {
            success: true,
            result: {
              eligible: true,
              consentRequired: false,
              memberAge,
              ageOfConsent: 18,
            },
          }
        }
      }
    },
    [dispatch, member, getMinorConsentByService],
  )

  return { checkServiceEligibility }
}
